/**
 * Form Field Component with Floating Label Support
 * 
 * A custom element that provides floating label functionality for form fields.
 * Supports input, select, and radio button elements.
 * 
 * @typedef {object} FormFieldRefs
 * @property {HTMLLabelElement|null} label - The label element
 * @property {HTMLElement|null} field - The field container that contains input, select, or radio elements
 * 
 * @extends {HTMLElement}
 * 
 * @attr {boolean} float-label - Enables floating label behavior (Material Design style)
 * 
 * @example
 * <!-- Input field with floating label -->
 * <form-field float-label>
 *   <div ref="field">
 *     <input type="text" name="email" id="email">
 *   </div>
 *   <label ref="label" for="email">Email Address</label>
 * </form-field>
 * 
 * @example
 * <!-- Select field with floating label -->
 * <form-field float-label>
 *   <div ref="field">
 *     <select name="country" id="country">
 *       <option value="">Select Country</option>
 *       <option value="us">United States</option>
 *     </select>
 *   </div>
 *   <label ref="label" for="country">Country</label>
 * </form-field>
 * 
 * @example
 * <!-- Radio button group with floating label -->
 * <form-field float-label>
 *   <div ref="field">
 *     <input type="radio" name="option" id="option1" value="1">
 *     <input type="radio" name="option" id="option2" value="2">
 *   </div>
 *   <label ref="label">Select Option</label>
 * </form-field>
 */
class FormField extends HTMLElement {
  /**
   * Get refs object for accessing DOM elements with ref attributes
   * @returns {FormFieldRefs}
   */
  get refs() {
    return {
      label: this.querySelector('[ref="label"]'),
      field: this.querySelector('[ref="field"]')
    };
  }

  /**
   * Observed attributes for the component
   */
  static get observedAttributes() {
    return ['float-label'];
  }

  connectedCallback() {
    if (!this.hasFloatLabel()) {
      return;
    }

    this.#initializeFloatingLabel();
    this.#attachEventListeners();
    this.#updateLabelState();
  }

  disconnectedCallback() {
    this.#removeEventListeners();
  }

  /**
   * Called when observed attributes change
   * 
   * @param {string} name - The attribute name
   * @param {string|null} oldValue - The old attribute value
   * @param {string|null} newValue - The new attribute value
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'float-label') {
      if (newValue !== null) {
        this.#initializeFloatingLabel();
        this.#attachEventListeners();
        this.#updateLabelState();
      } else {
        this.#removeEventListeners();
        this.#resetLabel();
      }
    }
  }

  /**
   * Check if float-label attribute is present
   */
  hasFloatLabel() {
    return this.hasAttribute('float-label');
  }

  /**
   * Initialize floating label functionality
   */
  #initializeFloatingLabel() {
    const { label, field } = this.refs;
    if (!label || !field) return;

    // Add floating label class to component
    this.classList.add('custom-form-field');
    
    // Add floating label class to label
    label.classList.add('form-field__label');
    
    // Find the actual form control (input, select, or radio)
    const formControl = this.#getFormControl();
    if (formControl) {
      // Ensure proper structure for floating label
      const labelFor = label.getAttribute('for');
      if (!formControl.hasAttribute('id') && labelFor) {
        formControl.setAttribute('id', labelFor);
      }
      
      // Add aria-label if label exists but no for attribute
      if (!label.getAttribute('for') && label.textContent.trim()) {
        formControl.setAttribute('aria-label', label.textContent.trim());
      }
    }
  }

  /**
   * Get the form control element (input, select, or radio)
   * @returns {HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement|null}
   */
  #getFormControl() {
    const { field } = this.refs;
    if (!field) return null;

    // Check for input, select, or textarea
    const input = field.querySelector('input:not([type="radio"]):not([type="checkbox"]), select, textarea');
    if (input instanceof HTMLInputElement || input instanceof HTMLSelectElement || input instanceof HTMLTextAreaElement) {
      return input;
    }

    // Check for radio buttons (return the first one for state checking)
    const radio = field.querySelector('input[type="radio"]');
    if (radio instanceof HTMLInputElement) {
      return radio;
    }

    return null;
  }

  /**
   * Get all form controls in the field (useful for radio groups)
   * @returns {Array<HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement>}
   */
  #getAllFormControls() {
    const { field } = this.refs;
    if (!field) return [];

    const inputs = Array.from(field.querySelectorAll('input:not([type="radio"]):not([type="checkbox"]), select, textarea'))
      .filter(el => el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement);
    const radios = Array.from(field.querySelectorAll('input[type="radio"]'))
      .filter(el => el instanceof HTMLInputElement);
    
    return inputs.length > 0 ? inputs : radios;
  }

  /**
   * Attach event listeners for floating label behavior
   */
  #attachEventListeners() {
    const formControls = this.#getAllFormControls();
    
    formControls.forEach(control => {
      // Handle focus events
      control.addEventListener('focus', this.#handleFocus);
      control.addEventListener('blur', this.#handleBlur);
      
      // Handle input/change events for value updates
      if (control instanceof HTMLSelectElement) {
        control.addEventListener('change', this.#handleChange);
      } else if (control instanceof HTMLInputElement && control.type === 'radio') {
        // For radio buttons, listen to change on all radios in the group
        control.addEventListener('change', this.#handleChange);
      } else if (control instanceof HTMLInputElement || control instanceof HTMLTextAreaElement) {
        control.addEventListener('input', this.#handleInput);
      }
    });
  }

  /**
   * Remove event listeners
   */
  #removeEventListeners() {
    const formControls = this.#getAllFormControls();
    
    formControls.forEach(control => {
      control.removeEventListener('focus', this.#handleFocus);
      control.removeEventListener('blur', this.#handleBlur);
      
      if (control instanceof HTMLSelectElement) {
        control.removeEventListener('change', this.#handleChange);
      } else if (control instanceof HTMLInputElement && control.type === 'radio') {
        control.removeEventListener('change', this.#handleChange);
      } else if (control instanceof HTMLInputElement || control instanceof HTMLTextAreaElement) {
        control.removeEventListener('input', this.#handleInput);
      }
    });
  }

  /**
   * Handle focus event
   */
  #handleFocus = () => {
    this.#updateLabelState(true);
  };

  /**
   * Handle blur event
   */
  #handleBlur = () => {
    this.#updateLabelState();
  };

  /**
   * Handle input event
   */
  #handleInput = () => {
    const formControl = this.#getFormControl();
    const isFocused = formControl ? document.activeElement === formControl : false;
    this.#updateLabelState(isFocused);
  };

  /**
   * Handle change event (for select and radio)
   */
  #handleChange = () => {
    this.#updateLabelState();
  };

  /**
   * Update label state based on field value and focus
   */
  #updateLabelState(isFocused = false) {
    const { label } = this.refs;
    if (!label) return;

    const formControl = this.#getFormControl();
    if (!formControl) return;

    const hasValue = this.#hasValue();
    const shouldFloat = hasValue || isFocused;

    if (shouldFloat) {
      label.classList.add('form-field__label--floating');
    } else {
      label.classList.remove('form-field__label--floating');
    }
  }

  /**
   * Check if the field has a value
   */
  #hasValue() {
    const formControl = this.#getFormControl();
    if (!formControl) return false;

    // Handle radio buttons - check if any in the group is checked
    if (formControl instanceof HTMLInputElement && formControl.type === 'radio') {
      const { field } = this.refs;
      if (!field) return false;
      
      const radioGroup = field.querySelectorAll(`input[type="radio"][name="${formControl.name}"]`);
      return Array.from(radioGroup).some(radio => radio instanceof HTMLInputElement && radio.checked);
    }

    // Handle select elements
    if (formControl instanceof HTMLSelectElement) {
      return formControl.value !== '' && formControl.value !== null;
    }

    // Handle file inputs
    if (formControl instanceof HTMLInputElement && formControl.type === 'file') {
      return formControl.files && formControl.files.length > 0;
    }

    // Handle input and textarea
    if (formControl instanceof HTMLInputElement || formControl instanceof HTMLTextAreaElement) {
      return formControl.value !== '' && formControl.value !== null;
    }

    return false;
  }

  /**
   * Reset label to initial state
   */
  #resetLabel() {
    const { label } = this.refs;
    if (!label) return;

    label.classList.remove('form-field__label--floating');
    this.classList.remove('custom-form-field');
  }
}

// Register the custom element
if (!customElements.get('form-field')) {
  customElements.define('form-field', FormField);
}

